import { FC, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { withPage } from '../../components/Page';
import { PrototypeBar } from '../../components/PrototypeBar';
import { QrLabelModal } from '../../components/QrLabelModal';
import { PhoneScannerModal } from '../../components/PhoneScannerModal';
import { PulseLogo } from '../../components/PulseLogo';
import { QrCodeSvg } from '../../components/QrCodeSvg';
import styles from './QrManagement.module.scss';

const QrManagement: FC = () => {
  const navigate = useNavigate();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isScannerOpen, setIsScannerOpen] = useState(false);
  const [toastMessage, setToastMessage] = useState<string | null>(null);

  const notify = (msg: string) => {
    setToastMessage(msg);
    setTimeout(() => {
      setToastMessage(null);
    }, 2600);
  };

  return (
    <main className={styles.container} tabIndex={-1}>
      <PrototypeBar />

      <div className={styles.pageTitle}>
        <div>
          <div className={styles.eyebrow}>Asset data / AHU-003 / QR code</div>
          <h1 className={styles.title}>QR code management</h1>
          <p className={styles.subtitle}>Manage the physical identity linked to AHU-003.</p>
        </div>
        <div className={styles.actions}>
          <button
            type="button"
            className={styles.secondaryBtn}
            onClick={() => setIsModalOpen(true)}
          >
            Preview label
          </button>
          <button
            type="button"
            className={styles.primaryBtn}
            onClick={() => notify('QR label download prepared')}
          >
            Download QR code
          </button>
        </div>
      </div>

      <div className={styles.qrLayout}>
        <div className={`${styles.card} ${styles.labelCard}`}>
          <div className={styles.physicalLabel}>
            <div className={styles.labelBrand}>
              <PulseLogo variant="stamp" theme="light" />
              <span className={styles.labelCategory}>Asset</span>
            </div>
            <h2>AHU-003</h2>
            <div className={styles.labelSub}>Air Handling Unit</div>
            <div className={styles.labelBottom}>
              <div className={styles.qr}>
                <QrCodeSvg />
              </div>
              <div className={styles.labelSite}>
                Commerzbank
                <br />
                Building A · Plant Room 01
              </div>
            </div>
          </div>
        </div>

        <div>
          <div className={`${styles.card} ${styles.qrDetails}`}>
            <h2 className={styles.detailsTitle}>QR code details</h2>
            <div className={styles.detailRow}>
              <span>Linked record</span>
              <b>AHU-003 · Air Handling Unit</b>
            </div>
            <div className={styles.detailRow}>
              <span>Location</span>
              <b>Building A · Plant Room 01</b>
            </div>
            <div className={styles.detailRow}>
              <span>QR code ID</span>
              <b>PUL-CBK-AHU-003-7F2A</b>
            </div>
            <div className={styles.detailRow}>
              <span>Status</span>
              <b style={{ color: '#147448' }}>● Active</b>
            </div>
            <div className={styles.detailRow}>
              <span>Created</span>
              <b>18 April 2016 · N. Kelly</b>
            </div>
            <div className={styles.detailRow}>
              <span>Last verified</span>
              <b>14 July 2026 · Mobile scan</b>
            </div>
            <div className={styles.actions} style={{ marginTop: '2.4rem' }}>
              <button
                type="button"
                className={styles.secondaryBtn}
                onClick={() => notify('Print dialog opened')}
              >
                Print label
              </button>
              <button
                type="button"
                className={styles.secondaryBtn}
                onClick={() => notify('A new secure QR code would be generated')}
              >
                Regenerate code
              </button>
              <button
                type="button"
                className={styles.secondaryBtn}
                onClick={() => setIsScannerOpen(true)}
              >
                📱 Scan with phone camera
              </button>
              <button
                type="button"
                className={styles.primaryBtn}
                onClick={() => navigate('/asset-data/ahu-003')}
              >
                View asset
              </button>
            </div>
          </div>

          <div className={styles.callout}>
            <b>Physical label guidance</b>
            Use a durable 50 × 50 mm label on the accessible front panel, adjacent to the existing asset identification tag. Scan the label using any standard smartphone camera to instantly open this record in Pulse.
          </div>
        </div>
      </div>

      <QrLabelModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} type="asset" />
      <PhoneScannerModal isOpen={isScannerOpen} onClose={() => setIsScannerOpen(false)} />

      {toastMessage && <div className={styles.toast}>{toastMessage}</div>}
    </main>
  );
};

export default withPage(QrManagement)({
  meta: {
    title: 'QR Code Management — AHU-003 · Pulse',
    description: 'Manage physical QR label identity for AHU-003.',
  },
  breadcrumbs: [
    { label: 'Pulse', href: '/' },
    { label: 'Commerzbank', href: '/' },
    { label: 'Asset data', href: '/asset-data/ahu-003' },
    { label: 'QR code', href: '/asset-data/ahu-003/qr' },
  ],
});
